/**
 * Exercices : génération, soumission, révision, historique, mini-cours, simulateur.
 *
 * Piège n°1 de la plateforme : toute génération ou soumission DOIT porter `userId`,
 * `learningMode` et `programmingLanguage`, sinon le backend retombe en silence sur
 * Unity/C#. Ici ces trois champs sont obligatoires dans les types : l'oubli devient
 * une erreur de compilation.
 */
import type {
  Difficulty,
  Exercise,
  ExerciseAttempt,
  ExerciseFeedback,
  ExerciseGenerationRequest,
  ExerciseWithAttempts,
  LearningMode,
  PaginatedResponse,
  ProgrammingLanguage,
  SubmitSolutionRequest,
} from '@unity-learning/shared';
import { apiGet, apiPost } from './client';

export type GenerateExerciseParams = Omit<ExerciseGenerationRequest, 'learningMode' | 'programmingLanguage'> & {
  userId: string;
  learningMode: LearningMode;
  programmingLanguage: ProgrammingLanguage;
  /** Nœud du parcours pour lequel l'exercice est généré (suivi de progression). */
  learningNodeId?: string;
  /** Points de connaissance ciblés (ids), s'il y en a. */
  knowledgePointIds?: string[];
};

export function generateExercise(params: GenerateExerciseParams): Promise<Exercise> {
  return apiPost<Exercise>('/api/exercises/generate', params);
}

export type SubmitExerciseParams = SubmitSolutionRequest & {
  learningMode: LearningMode;
  programmingLanguage: ProgrammingLanguage;
  learningNodeId?: string | null;
  knowledgePointIds?: string[];
};

export interface SubmitExerciseResult {
  attempt: ExerciseAttempt;
  feedback: ExerciseFeedback;
}

export function submitExercise(exerciseId: string, params: SubmitExerciseParams): Promise<SubmitExerciseResult> {
  return apiPost<SubmitExerciseResult>(`/api/exercises/${encodeURIComponent(exerciseId)}/submit`, params);
}

export function getExercise(exerciseId: string): Promise<Exercise> {
  return apiGet<Exercise>(`/api/exercises/${encodeURIComponent(exerciseId)}`);
}

export interface AttemptReview {
  exercise: Exercise;
  attempt: ExerciseAttempt;
  feedback: ExerciseFeedback | null;
}

/** Tentative passée avec son exercice et sa correction (mode révision). */
export function getAttemptReview(exerciseId: string, attemptId: string): Promise<AttemptReview> {
  return apiGet<AttemptReview>(
    `/api/exercises/${encodeURIComponent(exerciseId)}/attempts/${encodeURIComponent(attemptId)}`
  );
}

export interface ExerciseHistoryParams {
  learningMode: LearningMode;
  page?: number;
  limit?: number;
}

export function getExerciseHistory(
  userId: string,
  params: ExerciseHistoryParams
): Promise<PaginatedResponse<ExerciseWithAttempts>> {
  return apiGet<PaginatedResponse<ExerciseWithAttempts>>(`/api/exercises/history/${encodeURIComponent(userId)}`, {
    query: { learning_mode: params.learningMode, page: params.page, limit: params.limit },
  });
}

/** Suggestion adaptative de l'accueil (concept à retravailler ou à découvrir), `null` s'il n'y en a pas. */
export interface AdaptiveSuggestion {
  type: 'review' | 'discover';
  concept: string;
  conceptLabel: string;
  nodeId: string | null;
  difficulty: Difficulty;
  reason: string;
  stats: { attempts: number; successes: number; rate: number | null };
}

export function getExerciseSuggestion(userId: string, learningMode: LearningMode): Promise<AdaptiveSuggestion | null> {
  return apiGet<AdaptiveSuggestion | null>(`/api/exercises/suggestion/${encodeURIComponent(userId)}`, {
    query: { learning_mode: learningMode },
  });
}

/** Mini-cours enregistré pour un exercice (colonnes brutes de la table). */
export interface MiniCoursRecord {
  id: number;
  simplification_level: number;
  explanation: string;
  generated_at: string;
}

export function getExerciseMiniCours(exerciseId: string, userId: string): Promise<MiniCoursRecord[]> {
  return apiGet<MiniCoursRecord[]>(`/api/exercises/${encodeURIComponent(exerciseId)}/mini-cours`, {
    query: { userId },
  });
}

export interface CreateMiniCoursParams {
  userId: string;
  exerciseId: string;
  simplificationLevel: number;
  prompt: string;
}

export interface MiniCoursResult {
  explanation: string;
  simplificationLevel: number;
  fromCache?: boolean;
  fallback?: boolean;
}

/** Génère (1 appel Gemini) ou relit depuis le cache le mini-cours d'un niveau. */
export function createMiniCours(params: CreateMiniCoursParams): Promise<MiniCoursResult> {
  return apiPost<MiniCoursResult>('/api/exercises/mini-cours', params);
}

export interface MiniCoursLibraryItem {
  id: number;
  exerciseId: string;
  exerciseTitle: string | null;
  simplificationLevel: number;
  explanation: string;
  language: string;
  generatedAt: string;
}

export interface MiniCoursLibraryPage {
  items: MiniCoursLibraryItem[];
  total: number;
  limit: number;
  offset: number;
}

export function getMiniCoursLibrary(params: {
  learningMode: LearningMode;
  limit: number;
  offset?: number;
}): Promise<MiniCoursLibraryPage> {
  return apiGet<MiniCoursLibraryPage>('/api/exercises/mini-cours/library', {
    query: { learning_mode: params.learningMode, limit: params.limit, offset: params.offset },
  });
}

export interface SimResultParams {
  userId: string;
  solution: string;
  learningNodeId?: string | null;
  /** Verdict déterministe du simulateur (`VerifyResult`), validé côté serveur. */
  result: unknown;
  timeSpent: number;
  learningMode: LearningMode;
  programmingLanguage: ProgrammingLanguage;
}

export interface SimResultOutcome {
  score: number;
  isCorrect: boolean;
  missingKpCodes?: string[];
}

export function saveSimResult(exerciseId: string, params: SimResultParams): Promise<SimResultOutcome> {
  return apiPost<SimResultOutcome>(`/api/exercises/${encodeURIComponent(exerciseId)}/sim-result`, params);
}
